import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiDownload, FiPrinter, FiRotateCcw, FiSearch, FiFileText, FiUsers } from 'react-icons/fi';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import useCurrency from '../../hooks/useCurrency';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const POSTED_STATUSES = ['Posted', 'Paid'];
const monthLabel = (m) => {
  if (!m) return '';
  const [y, mn] = m.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(mn, 10) - 1]} ${y}`;
};

const SalaryRegister = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + loaded rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('salary-register');
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const [runs, setRuns] = useState(() => savedMem?.runs ?? []);
  const [loading, setLoading] = useState(() => savedMem ? false : true);
  const [financialYears, setFinancialYears] = useState([]);

  // Filters
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [monthFilter, setMonthFilter] = useState(() => savedMem?.monthFilter ?? '');  // YYYY-MM or empty
  const [departmentFilter, setDepartmentFilter] = useState(() => savedMem?.departmentFilter ?? 'all');
  const [staffSearch, setStaffSearch] = useState(() => savedMem?.staffSearch ?? '');

  // Print config (for payslip)
  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => {
    if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig);
  }, [activeCompany]);

  // ─── Load FYs and payroll runs ───
  const loadFYs = useCallback(async () => {
    if (!activeCompany?.id) return;
    const snap = await getDocs(query(
      collection(db, 'companies', activeCompany.id, 'financialYears'),
      orderBy('startDate', 'desc')
    ));
    const fys = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setFinancialYears(fys);
    // Fresh visit only — a restored snapshot keeps the user's chosen year.
    if (!savedMem && !selectedFYId) setSelectedFYId(activeFinancialYear?.id || fys[0]?.id || '');
  }, [activeCompany, activeFinancialYear, selectedFYId, savedMem]);

  const loadRuns = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'companies', activeCompany.id, 'transactions'),
        where('tranTypeCode', '==', 'PAY'),
        where('financialYearId', '==', selectedFYId)
      ));
      let txs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => POSTED_STATUSES.includes(t.status));
      if (activeBranch && activeBranch.id !== 'all') {
        txs = txs.filter(t => t.branchId === activeBranch.id);
      }
      setRuns(txs);
    } catch (e) { console.error('Load PAY error:', e); }
    finally { setLoading(false); }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => { loadFYs(); }, [loadFYs]);
  // On Back (restored), SKIP ONLY the first auto-load — restored rows already hold
  // the data (0 re-reads) — but still let a later Financial-Year change re-fetch.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    loadRuns();
  }, [loadRuns]);

  // Remember this view (filters + loaded rows) so Back restores it with 0 re-reads.
  // Month/Department/Search are display-only over already-loaded runs, so we keep
  // the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (loading) return;
    saveMemory({ runs, selectedFYId, monthFilter, departmentFilter, staffSearch });
  }, [loading, runs, selectedFYId, monthFilter, departmentFilter, staffSearch, saveMemory]);

  const resetFilters = () => {
    setMonthFilter('');
    setDepartmentFilter('all');
    setStaffSearch('');
    setSelectedFYId(activeFinancialYear?.id || financialYears[0]?.id || '');
  };

  // ─── Flatten PAY transactions into per-staff rows ───
  const flatRows = useMemo(() => {
    const rows = [];
    runs.forEach(run => {
      if (monthFilter && run.payrollMonth !== monthFilter) return;
      (run.payrollLines || []).forEach(line => {
        if (departmentFilter !== 'all' && line.department !== departmentFilter) return;
        if (staffSearch) {
          const q = staffSearch.toLowerCase();
          const m = (line.staffName || '').toLowerCase().includes(q) ||
                    (line.staffCode || '').toLowerCase().includes(q);
          if (!m) return;
        }
        rows.push({
          ...line,
          payrollDocNumber: run.docNumber,
          payrollId: run.id,
          payrollMonth: run.payrollMonth,
          payrollDate: run.docDate,
          payrollStatus: run.status,
          run
        });
      });
    });
    return rows.sort((a, b) => (b.payrollMonth || '').localeCompare(a.payrollMonth || '') ||
                                (a.staffCode || '').localeCompare(b.staffCode || ''));
  }, [runs, monthFilter, departmentFilter, staffSearch]);

  const departments = useMemo(() => {
    const set = new Set();
    runs.forEach(r => (r.payrollLines || []).forEach(l => l.department && set.add(l.department)));
    return [...set].sort();
  }, [runs]);

  const months = useMemo(() => {
    const set = new Set();
    runs.forEach(r => r.payrollMonth && set.add(r.payrollMonth));
    return [...set].sort().reverse();
  }, [runs]);

  // ─── KPIs ───
  const kpis = useMemo(() => {
    let staffCount = flatRows.length;
    let gross = 0, statutory = 0, loan = 0, advance = 0, net = 0;
    flatRows.forEach(r => {
      gross += Number(r.adjustedGross) || 0;
      statutory += Number(r.statutoryTotal) || 0;
      loan += Number(r.loanInstallment) || 0;
      advance += Number(r.advanceRecovery) || 0;
      net += Number(r.netPayable) || 0;
    });
    return { staffCount, gross, statutory, loan, advance, net };
  }, [flatRows]);

  // ─── Payslip print ───
  const handlePrintPayslip = (row) => {
    printDocument('payslip', {
      ...row.run,
      line: row,
      payrollMonth: row.payrollMonth,
      docNumber: row.payrollDocNumber
    }, activeCompany, printConfig, activeBranch);
  };

  // ─── Excel ───
  const exportToExcel = () => {
    const data = flatRows.map(r => ({
      'Payroll #': r.payrollDocNumber,
      'Month': monthLabel(r.payrollMonth),
      'Date': r.payrollDate,
      'Staff Code': r.staffCode,
      'Staff Name': r.staffName,
      'Designation': r.designation,
      'Department': r.department,
      'Working Days': r.totalWorkingDays,
      'Present': r.present,
      'Absent': r.absent,
      'Paid Leave': r.paidLeave,
      'Payable Days': r.payableDays,
      'Basic': r.basicProrated,
      'Allowances': r.allowancesTotal,
      'Effective Gross': r.effectiveGross,
      'Bonus + Adj': (r.bonus || 0) + (r.arrears || 0) + (r.overtime || 0) - (r.leaveDeduction || 0),
      'Adjusted Gross': r.adjustedGross,
      'Statutory': r.statutoryTotal,
      'Loan Recovery': r.loanInstallment,
      'Advance Recovery': r.advanceRecovery,
      'Net Payable': r.netPayable,
      'Status': r.payrollStatus
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Salary Register');
    XLSX.writeFile(wb, `SalaryRegister_${monthFilter || selectedFYId}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const printReport = () => {
    const company = activeCompany || {};
    const branch = activeBranch && activeBranch.id !== 'all' ? activeBranch : null;
    const fy = financialYears.find(f => f.id === selectedFYId);
    const accent = '#800020';

    const escape = (str) => String(str ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    const filterParts = [
      `<strong>Period:</strong> ${monthFilter ? monthLabel(monthFilter) : 'All months in ' + escape(fy?.yearName || fy?.yearCode || '')}`,
      departmentFilter !== 'all' ? `<strong>Department:</strong> ${escape(departmentFilter)}` : null,
      staffSearch ? `<strong>Staff:</strong> ${escape(staffSearch)}` : null,
      branch ? `<strong>Branch:</strong> ${escape(branch.branchName)}` : null,
    ].filter(Boolean).join(' &nbsp;|&nbsp; ');

    const rowsHtml = flatRows.map(r => `
      <tr>
        <td>${monthLabel(r.payrollMonth)}</td>
        <td><strong>${escape(r.payrollDocNumber)}</strong></td>
        <td>${escape(r.staffCode || '')}</td>
        <td>${escape(r.staffName || '')}</td>
        <td>${escape(r.designation || '')}</td>
        <td>${escape(r.department || '')}</td>
        <td class="c">${r.totalWorkingDays || 0}</td>
        <td class="c">${r.present || 0}</td>
        <td class="c">${r.absent || 0}</td>
        <td class="c">${r.paidLeave || 0}</td>
        <td class="c">${r.payableDays || 0}</td>
        <td class="r">${formatAmount(r.basicProrated || 0)}</td>
        <td class="r">${formatAmount(r.allowancesTotal || 0)}</td>
        <td class="r"><strong>${formatAmount(r.adjustedGross || 0)}</strong></td>
        <td class="r warn">${formatAmount(r.statutoryTotal || 0)}</td>
        <td class="r warn">${formatAmount(r.loanInstallment || 0)}</td>
        <td class="r warn">${formatAmount(r.advanceRecovery || 0)}</td>
        <td class="r net">${formatAmount(r.netPayable || 0)}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<title>Salary Register${monthFilter ? ' - ' + monthLabel(monthFilter) : ''}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',sans-serif;color:#1A1A1A;padding:24px;line-height:1.4}
  @page{size:A4 landscape;margin:12mm}
  @media print{body{padding:0}}

  .header{display:flex;justify-content:space-between;align-items:flex-start;
    border-bottom:2px solid ${accent};padding-bottom:12px;margin-bottom:18px}
  .company-block .company-name{font-size:18px;font-weight:700;color:${accent}}
  .company-block .branch-name{font-size:13px;color:#444;font-weight:500}
  .meta{font-size:11px;color:#666;margin-top:2px}
  .title-block{text-align:right}
  .doc-title{color:${accent};font-size:22px;font-weight:700;letter-spacing:1px}

  .filters{font-size:12px;color:#444;margin-bottom:14px;padding:8px 12px;
    background:#f7f7f7;border-left:3px solid ${accent};border-radius:3px}

  .kpi-row{display:flex;gap:8px;margin-bottom:16px}
  .kpi{flex:1;padding:8px 10px;border:1px solid #ddd;border-radius:4px;text-align:center;background:white}
  .kpi.highlight{background:${accent};color:white;border-color:${accent}}
  .kpi-label{font-size:9px;color:#666;text-transform:uppercase;letter-spacing:0.4px;font-weight:600}
  .kpi.highlight .kpi-label{color:rgba(255,255,255,0.85)}
  .kpi-value{font-size:14px;font-weight:700;font-family:monospace;margin-top:3px}

  table{width:100%;border-collapse:collapse;font-size:10px}
  th{background:${accent};color:white;padding:7px 5px;text-align:left;
    font-weight:600;text-transform:uppercase;letter-spacing:0.3px;font-size:9px;white-space:nowrap}
  td{padding:5px;border-bottom:1px solid #e8e8e8}
  tr:nth-child(even) td{background:#fafafa}
  .c{text-align:center}
  .r{text-align:right;font-family:monospace}
  .warn{color:#b8860b}
  .net{font-weight:700;color:#1e7e34}
  tfoot td{background:#f0f0f0;font-weight:700;border-top:2px solid ${accent};font-size:11px}

  .empty{text-align:center;padding:60px;color:#999;font-style:italic}
  .footer{margin-top:24px;padding-top:8px;border-top:1px solid #ddd;
    font-size:10px;color:#888;text-align:center}
</style>
</head>
<body>
  <div class="header">
    <div class="company-block">
      ${branch
        ? `<div class="company-name">${escape(branch.branchName || '')}</div>
           <div class="branch-name">${escape(company.companyName || 'Company')}</div>`
        : `<div class="company-name">${escape(company.companyName || 'Company')}</div>`}
      ${(company.address || company.city) ? `<div class="meta">${escape([company.address, company.city].filter(Boolean).join(', '))}</div>` : ''}
      ${(company.ntn || company.gstNo) ? `<div class="meta">${[
          company.ntn ? ((company.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN') + ': ' + escape(company.ntn) : '',
          company.gstNo ? (escape(company.taxLabel || 'GST') + ': ' + escape(company.gstNo)) : ''
        ].filter(Boolean).join(' &nbsp;|&nbsp; ')}</div>` : ''}
    </div>
    <div class="title-block">
      <div class="doc-title">SALARY REGISTER</div>
      <div class="meta">Generated: ${new Date().toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}</div>
    </div>
  </div>

  <div class="filters">${filterParts}</div>

  <div class="kpi-row">
    <div class="kpi"><div class="kpi-label">Lines</div><div class="kpi-value">${kpis.staffCount}</div></div>
    <div class="kpi"><div class="kpi-label">Total Gross</div><div class="kpi-value">${formatAmount(kpis.gross)}</div></div>
    <div class="kpi"><div class="kpi-label">Statutory</div><div class="kpi-value">${formatAmount(kpis.statutory)}</div></div>
    <div class="kpi"><div class="kpi-label">Loan Recovery</div><div class="kpi-value">${formatAmount(kpis.loan)}</div></div>
    <div class="kpi"><div class="kpi-label">Advance Recovery</div><div class="kpi-value">${formatAmount(kpis.advance)}</div></div>
    <div class="kpi highlight"><div class="kpi-label">Net Paid</div><div class="kpi-value">${formatAmount(kpis.net)}</div></div>
  </div>

  ${flatRows.length === 0
    ? `<div class="empty">No payroll lines match the current filters.</div>`
    : `<table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Payroll #</th>
            <th>Staff Code</th>
            <th>Staff Name</th>
            <th>Designation</th>
            <th>Department</th>
            <th class="c">Working Days</th>
            <th class="c">Present</th>
            <th class="c">Absent</th>
            <th class="c">Paid Leave</th>
            <th class="c">Payable Days</th>
            <th class="r">Basic</th>
            <th class="r">Allowances</th>
            <th class="r">Adj. Gross</th>
            <th class="r">Statutory</th>
            <th class="r">Loan</th>
            <th class="r">Advance</th>
            <th class="r">Net Payable</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot>
          <tr>
            <td colspan="13" class="r">TOTALS:</td>
            <td class="r">${formatAmount(kpis.gross)}</td>
            <td class="r warn">${formatAmount(kpis.statutory)}</td>
            <td class="r warn">${formatAmount(kpis.loan)}</td>
            <td class="r warn">${formatAmount(kpis.advance)}</td>
            <td class="r net" style="font-size:12px">${formatAmount(kpis.net)}</td>
          </tr>
        </tfoot>
      </table>`
  }

  <div class="footer">
    Salary Register · ${flatRows.length} line${flatRows.length !== 1 ? 's' : ''} · Printed from ${escape(company.companyName || 'AMAL ERP')}
  </div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) { alert('Please allow pop-ups to print this report.'); return; }
    w.document.write(html);
    addPrintBrand(w);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 100);
  };

  return (
    <PageLayout pageTitle="Salary Register" breadcrumb="Reports / Salary / Salary Register">
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Financial Year</FilterLabel>
            <FilterSelect value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              {financialYears.map(fy => <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}</option>)}
            </FilterSelect>
          </FilterItem>
          <FilterItem>
            <FilterLabel>Month</FilterLabel>
            <FilterSelect value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">All Months</option>
              {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </FilterSelect>
          </FilterItem>
          <FilterItem>
            <FilterLabel>Department</FilterLabel>
            <FilterSelect value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
              <option value="all">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </FilterSelect>
          </FilterItem>
          <FilterItem style={{ minWidth: 220 }}>
            <FilterLabel>Search Staff</FilterLabel>
            <FilterInputWrap>
              <FiSearch />
              <input type="text" placeholder="Code or name..."
                value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} />
            </FilterInputWrap>
          </FilterItem>
          <FilterItem>
            <FilterLabel>&nbsp;</FilterLabel>
            <ResetBtn onClick={resetFilters}><FiRotateCcw /> Reset</ResetBtn>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      <KPIGrid>
        <KPI><KL>Lines</KL><KV>{kpis.staffCount}</KV></KPI>
        <KPI><KL>Total Gross</KL><KV>{formatCurrency(kpis.gross)}</KV></KPI>
        <KPI><KL>Statutory</KL><KV>{formatCurrency(kpis.statutory)}</KV></KPI>
        <KPI><KL>Loan Recovery</KL><KV>{formatCurrency(kpis.loan)}</KV></KPI>
        <KPI><KL>Advance Recovery</KL><KV>{formatCurrency(kpis.advance)}</KV></KPI>
        <KPI $highlight><KL>Net Paid</KL><KV>{formatCurrency(kpis.net)}</KV></KPI>
      </KPIGrid>

      <ToolbarRow>
        <h3 style={{ color: colors.text.secondary, fontWeight: 600 }}>
          {flatRows.length} payroll line{flatRows.length !== 1 ? 's' : ''}
        </h3>
        <div style={{ display: 'flex', gap: spacing.sm }}>
          <ExportBtn onClick={exportToExcel}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={printReport}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? <Loading>Loading...</Loading> : flatRows.length === 0 ? (
        <Empty>
          <EmptyIcon><FiFileText /></EmptyIcon>
          <p>No payroll lines match your filters. Run a payroll under <strong>Salary → Payroll Run</strong> first.</p>
        </Empty>
      ) : (
        <TableCard>
          <Table>
            <thead>
              <tr>
                <Th>Month</Th>
                <Th>Payroll #</Th>
                <Th>Staff</Th>
                <Th>Department</Th>
                <Th style={{ textAlign: 'center' }}>Days (P/A/PL)</Th>
                <Th style={{ textAlign: 'right' }}>Basic</Th>
                <Th style={{ textAlign: 'right' }}>Allowances</Th>
                <Th style={{ textAlign: 'right' }}>Adj. Gross</Th>
                <Th style={{ textAlign: 'right' }}>Statutory</Th>
                <Th style={{ textAlign: 'right' }}>Loan</Th>
                <Th style={{ textAlign: 'right' }}>Advance</Th>
                <Th style={{ textAlign: 'right' }}>Net</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {flatRows.map((r, i) => (
                <Tr key={`${r.payrollId}_${r.staffId}_${i}`}>
                  <Td><MonthBadge>{monthLabel(r.payrollMonth)}</MonthBadge></Td>
                  <Td><strong onClick={() => navigateToTransaction(navigate, 'PAY', r.payrollId)} title="Open payroll run" style={{ color: colors.primary.main, cursor: 'pointer' }}>{r.payrollDocNumber}</strong></Td>
                  <Td>
                    <div>{r.staffName}</div>
                    <Sub>{r.staffCode}{r.designation ? ` · ${r.designation}` : ''}</Sub>
                  </Td>
                  <Td>{r.department || '-'}</Td>
                  <Td style={{ textAlign: 'center' }}>{r.present || 0}/{r.absent || 0}/{r.paidLeave || 0}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatCurrency(r.basicProrated || 0)}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatCurrency(r.allowancesTotal || 0)}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(r.adjustedGross || 0)}</Td>
                  <Td style={{ textAlign: 'right', color: colors.warning.dark }}>−{formatCurrency(r.statutoryTotal || 0)}</Td>
                  <Td style={{ textAlign: 'right', color: colors.warning.dark }}>−{formatCurrency(r.loanInstallment || 0)}</Td>
                  <Td style={{ textAlign: 'right', color: colors.warning.dark }}>−{formatCurrency(r.advanceRecovery || 0)}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 700, color: colors.success.dark }}>{formatCurrency(r.netPayable || 0)}</Td>
                  <Td>
                    <PrintBtn title="Print Payslip" onClick={() => handlePrintPayslip(r)}>
                      <FiPrinter />
                    </PrintBtn>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableCard>
      )}
    </PageLayout>
  );
};

// ─── Styles ───
const FiltersCard = styled.div`background: ${colors.background.default}; border-radius: ${borderRadius.lg}; padding: ${spacing.md}; box-shadow: ${shadows.sm}; margin-bottom: ${spacing.md};`;
const FilterGrid = styled.div`display: flex; flex-wrap: wrap; gap: ${spacing.md}; align-items: flex-end;`;
const FilterItem = styled.div`display: flex; flex-direction: column; gap: 4px; min-width: 140px; flex: 1;`;
const FilterLabel = styled.label`font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.5px;`;
const FilterSelect = styled.select`padding: ${spacing.sm}; border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm}; font-size: ${typography.fontSize.sm}; background: ${colors.background.default};`;
const FilterInputWrap = styled.div`
  display: flex; align-items: center; gap: ${spacing.sm};
  padding: 0 ${spacing.sm}; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm}; color: ${colors.text.secondary};
  input { border: none; outline: none; background: transparent; flex: 1; padding: ${spacing.sm} 0; }
`;
const ResetBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.neutral[100]};
  border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm}; cursor: pointer;
`;
const KPIGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${spacing.md}; margin-bottom: ${spacing.md};
`;
const KPI = styled.div`
  background: ${props => props.$highlight ? colors.primary.main : colors.background.default};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  padding: ${spacing.md}; border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm};
  border-left: 3px solid ${props => props.$highlight ? colors.primary.dark : colors.border.light};
`;
const KL = styled.div`font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;`;
const KV = styled.div`font-size: 20px; font-weight: 700; font-family: monospace;`;
const ToolbarRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: ${spacing.md}; flex-wrap: wrap; gap: ${spacing.md};`;
const ExportBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main}; color: white; border: none;
  border-radius: ${borderRadius.sm}; cursor: pointer; font-weight: 600; font-size: ${typography.fontSize.sm};
  &:hover { background: ${colors.primary.dark}; }
`;
const TableCard = styled.div`background: ${colors.background.default}; border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm}; overflow: auto;`;
const Table = styled.table`width: 100%; border-collapse: collapse; min-width: 1200px;`;
const Th = styled.th`
  padding: ${spacing.md}; text-align: left; background: ${colors.neutral[50]}; color: ${colors.text.secondary};
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.border.light}; white-space: nowrap;
`;
const Tr = styled.tr`&:hover { background: ${colors.neutral[50]}; }`;
const Td = styled.td`padding: ${spacing.sm} ${spacing.md}; font-size: ${typography.fontSize.sm}; border-bottom: 1px solid ${colors.neutral[100]};`;
const Sub = styled.div`font-size: 11px; color: ${colors.text.tertiary}; margin-top: 2px;`;
const MonthBadge = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${colors.primary.main}15; color: ${colors.primary.main};
  border-radius: ${borderRadius.xl}; font-size: 11px; font-weight: 700;
`;
const Loading = styled.div`padding: ${spacing.xxl}; text-align: center; color: ${colors.text.secondary};`;
const Empty = styled.div`
  padding: ${spacing.xxl}; text-align: center; background: ${colors.background.default};
  border-radius: ${borderRadius.lg}; display: flex; flex-direction: column; align-items: center; gap: ${spacing.md};
  color: ${colors.text.secondary};
`;
const EmptyIcon = styled.div`
  width: 80px; height: 80px; border-radius: 50%;
  background: ${colors.primary.main}15; color: ${colors.primary.main};
  display: flex; align-items: center; justify-content: center; font-size: 36px;
`;
const PrintBtn = styled.button`
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm}; cursor: pointer; color: ${colors.text.secondary};
  &:hover { background: ${colors.primary.main}; color: white; border-color: transparent; }
`;

export default SalaryRegister;
